import { isNoSecondTry } from '../lib/workItems.js';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ApiRequestError,
  approveItem,
  cancelItem,
  fetchBatchRuns,
  fetchCourses,
  fetchGroups,
  fetchStructureSubtree,
  fetchQueueItems,
  sourceDocumentHref,
  type GroupNode,
  type SessionIdentity,
} from '../api/client.js';
import {
  buildProposedTree,
  buildTreeDiff,
  toTreeNodeId,
  type TreeDiffEntry,
  type TreeNode,
} from '../lib/treeDiff.js';
import type { DiffOp } from '../lib/treeDiff.js';
import { sourceFilePage } from '../lib/sources.js';
import { courseTitleWithKey, selectAuthoringCourseKey } from '../lib/courseDefaults.js';
import { readCourseParam } from '../lib/courseParam.js';
import { PRIMARY_ACTION_LINK_STYLES } from '../lib/actionStyles.js';
import type { SourceRef, WorkItemSummary } from '../types/workflow.js';

type ReviewBanner = {
  tone: 'error' | 'success';
  message: string;
};

type StructuralWorkflowUnavailable = {
  courseKey: string;
  courseTitle: string;
  defaultCourseKey: string;
};

type SelectedNodeReview = {
  node: TreeNode;
  appliedDiff: TreeDiffEntry | null;
  diffOp: DiffOp;
  title: string;
  rationale: string;
  sourceRefs: SourceRef[];
  confidence: number | null;
  clusterKey: string | null;
  inheritedFromTitle: string | null;
  isDirect: boolean;
};

type TreeIndex = Map<string, { node: TreeNode; parentId: string | null }>;

export function StructuralDiffReview({ session }: { session?: SessionIdentity }) {
  const [params] = useSearchParams();
  const [items, setItems] = useState<WorkItemSummary[]>([]);
  const [baseTree, setBaseTree] = useState<TreeNode[]>([]);
  const [courseTitle, setCourseTitle] = useState('');
  const [currentVersionLabel, setCurrentVersionLabel] = useState('Current Baseline');
  const [proposedVersionLabel, setProposedVersionLabel] = useState('Proposed Run');
  const [currentChangeIndex, setCurrentChangeIndex] = useState(0);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState<ReviewBanner | null>(null);
  const [structuralWorkflowUnavailable, setStructuralWorkflowUnavailable] =
    useState<StructuralWorkflowUnavailable | null>(null);
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(() => new Set());
  // Either spelling — the course browser writes `?courseId`. See `lib/courseParam.ts`.
  const courseKey = readCourseParam(params) ?? '';
  const sectionId = params.get('sectionId') ?? '';
  const clusterKey = params.get('clusterKey') ?? undefined;

  useEffect(() => {
    void loadReview();
  }, [clusterKey, courseKey, sectionId]);

  const diffs = useMemo(() => buildTreeDiff(baseTree, items), [baseTree, items]);
  const proposedTree = useMemo(() => buildProposedTree(baseTree, diffs), [baseTree, diffs]);
  const currentTreeIndex = useMemo(() => buildTreeIndex(baseTree), [baseTree]);
  const proposedTreeIndex = useMemo(() => buildTreeIndex(proposedTree), [proposedTree]);
  const diffIndex = useMemo(
    () => new Map(diffs.flatMap((entry) => (entry.targetId ? [[entry.targetId, entry] as const] : []))),
    [diffs],
  );
  const current = diffs[currentChangeIndex];
  const selectedReview = useMemo(
    () => resolveSelectedNodeReview(selectedNodeId, currentTreeIndex, proposedTreeIndex, diffIndex),
    [selectedNodeId, currentTreeIndex, proposedTreeIndex, diffIndex],
  );

  useEffect(() => {
    setCurrentChangeIndex((index) => Math.min(index, Math.max(diffs.length - 1, 0)));
  }, [diffs.length]);

  useEffect(() => {
    if (selectedNodeId && (currentTreeIndex.has(selectedNodeId) || proposedTreeIndex.has(selectedNodeId))) {
      return;
    }

    const fallbackNodeId =
      current?.targetId ??
      diffs[0]?.targetId ??
      findFirstSelectableNodeId(proposedTree) ??
      findFirstSelectableNodeId(baseTree) ??
      null;

    if (fallbackNodeId !== selectedNodeId) {
      setSelectedNodeId(fallbackNodeId);
    }
  }, [baseTree, current?.targetId, currentTreeIndex, diffs, proposedTree, proposedTreeIndex, selectedNodeId]);

  useEffect(() => {
    if (!selectedNodeId) return;
    // After React re-renders with the new selection, scroll both panels to show the selected node
    requestAnimationFrame(() => {
      document.querySelectorAll('[data-testid="current-structure-panel"], [data-testid="proposed-structure-panel"]').forEach((panel) => {
        const selected = panel.querySelector<HTMLElement>('[data-selected="true"]');
        if (selected && typeof selected.scrollIntoView === 'function') {
          selected.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
    });
  }, [selectedNodeId]);

  if (loading) {
    return <ReviewState message="Loading structural review..." tone="neutral" />;
  }

  if (error) {
    return <ReviewState message={error} tone="error" />;
  }

  if (structuralWorkflowUnavailable) {
    return (
      <ReviewState
        actionHref={`/queue/structural?courseKey=${encodeURIComponent(structuralWorkflowUnavailable.defaultCourseKey)}`}
        actionLabel="Open CFA 2027 structural review"
        message={`Structural review is not configured for ${structuralWorkflowUnavailable.courseTitle} (${structuralWorkflowUnavailable.courseKey}). Use the course queue for this course, or switch to CFA 2027 for curriculum-update structural work.`}
        tone="neutral"
      />
    );
  }

  if (!courseKey) {
    return <ReviewState message="Structural review requires a course context." tone="error" />;
  }

  const hasDiffs = diffs.length > 0;
  const donutCircumference = 175.9;
  const displayedReview = hasDiffs
    ? (selectedReview ?? {
        node: {
          id: current!.targetId ?? 'review',
          title: current!.proposedTitle ?? current!.currentTitle ?? 'Selected change',
          parentId: null,
          ordinal: 0,
          type: current!.targetType,
          children: [],
        },
        appliedDiff: current!,
        diffOp: current!.diffOp,
        title: current!.proposedTitle ?? current!.currentTitle ?? 'Selected change',
        rationale: current!.rationale,
        sourceRefs: current!.sourceRefs,
        confidence: current!.confidence,
        clusterKey: current!.clusterKey,
        isDirect: true,
      })
    : null;
  const confidence = displayedReview?.confidence ?? 0;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white">
      <div className="flex h-14 flex-shrink-0 items-center justify-between border-b border-slate-200 bg-white px-6">
        <nav className="flex items-center gap-2 text-sm text-slate-500">
          <Link
            className="font-medium text-slate-500 hover:text-primary"
            to={`/queue?courseKey=${encodeURIComponent(courseKey)}${sectionId ? `&sectionId=${encodeURIComponent(sectionId)}` : ''}`}
          >
            Back to Queue
          </Link>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <Link
            className="font-medium text-slate-500 hover:text-primary"
            to={`/queue/structural?courseKey=${encodeURIComponent(courseKey)}`}
          >
            {courseTitleWithKey(courseTitle ? { id: courseKey, title: courseTitle } : undefined, courseKey)}
          </Link>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <span className="font-semibold text-slate-900">
            {!hasDiffs
              ? 'No pending changes'
              : clusterKey
                ? `${currentChangeIndex + 1} of ${diffs.length} in cluster`
                : `${currentChangeIndex + 1} of ${diffs.length} structural changes`}
          </span>
        </nav>
        {hasDiffs ? (
          <div className="flex items-center gap-3">
            <button
              className="px-4 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
              onClick={() => {
                void handleCancel();
              }}
              type="button"
            >
              Reject draft
            </button>
            <button
              className="cursor-not-allowed rounded border border-slate-200 px-4 py-1.5 text-sm font-semibold text-slate-400"
              disabled
              title="Approve with Edit is unavailable until structural patch editing supports full-field changes."
              type="button"
            >
              Approve with Edit
            </button>
            <button
              className="rounded bg-[#10b981] px-5 py-1.5 text-sm font-semibold text-white transition-colors hover:opacity-90"
              onClick={() => {
                void handleApprove();
              }}
              type="button"
            >
              Approve
            </button>
            {diffs.length > 1 ? (
              <button
                className="flex items-center gap-1.5 rounded bg-primary px-5 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
                onClick={() => {
                  void handleApproveAll();
                }}
                type="button"
              >
                <span className="material-symbols-outlined text-[16px]">done_all</span>
                Approve All ({diffs.length})
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {banner ? (
        <div className={banner.tone === 'error'
          ? 'border-b border-rose-200 bg-rose-50 px-6 py-3 text-sm text-rose-800'
          : 'border-b border-emerald-200 bg-emerald-50 px-6 py-3 text-sm text-emerald-800'}>
          {banner.message}
        </div>
      ) : null}

      <main className="flex min-h-0 flex-1 overflow-hidden">
        <CurrentTreePanel
          currentVersionLabel={currentVersionLabel}
          expandedNodeIds={expandedNodeIds}
          nodes={baseTree}
          onSelect={handleNodeSelect}
          onToggle={toggleNodeExpansion}
          selectedNodeId={selectedNodeId}
        />
        <ProposedTreePanel
          proposedVersionLabel={proposedVersionLabel}
          diffIndex={diffIndex}
          expandedNodeIds={expandedNodeIds}
          nodes={proposedTree}
          onSelect={handleNodeSelect}
          onToggle={toggleNodeExpansion}
          selectedNodeId={selectedNodeId}
        />
        <div className="flex w-[35%] flex-col overflow-y-auto bg-white">
          {!displayedReview ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
              <span className="material-symbols-outlined text-4xl text-emerald-400">check_circle</span>
              <h3 className="text-lg font-bold text-primary">No pending changes</h3>
              <p className="text-sm text-slate-500">
                All structural changes for this course have been reviewed. Select a node in the tree to inspect its current state.
              </p>
            </div>
          ) : (
          <div className="space-y-8 p-6">
            <section>
              <div className="mb-4 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-lg font-bold text-primary">
                  Rationale for: {displayedReview.title}
                </h3>
                <span className={diffOpBadgeClass(displayedReview.diffOp)}>
                  {displayedReview.diffOp.toUpperCase()}
                </span>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                <p className="text-sm leading-relaxed text-slate-700">{displayedReview.rationale}</p>
                {!displayedReview.isDirect && displayedReview.inheritedFromTitle ? (
                  <p className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                    Inherited from {displayedReview.inheritedFromTitle}
                  </p>
                ) : null}
              </div>
            </section>

            <section>
              <h4 className="mb-3 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                Source References {displayedReview.sourceRefs.length > 0 ? `(${displayedReview.sourceRefs.length})` : ''}
              </h4>
              {displayedReview.sourceRefs.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {displayedReview.sourceRefs.map((ref, refIndex) => {
                    const filePage = sourceFilePage(ref);
                    const sourceBody = (
                      <>
                        <div className={`flex h-10 w-10 items-center justify-center rounded ${ref.path.endsWith('.pdf') ? 'bg-red-50' : 'bg-slate-50'}`}>
                          <span className={`material-symbols-outlined ${ref.path.endsWith('.pdf') ? 'text-red-500' : 'text-slate-400'}`}>
                            {ref.path.endsWith('.pdf') ? 'picture_as_pdf' : 'description'}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-slate-900">{ref.path}</div>
                          <div className="text-xs text-slate-500">
                            {filePage != null ? `Page ${filePage}` : ''}
                            {ref.note ? `${filePage != null ? ' · ' : ''}${ref.note}` : ''}
                            {ref.availability === 'missing' ? `${filePage != null || ref.note ? ' · ' : ''}Source not ingested` : ''}
                          </div>
                        </div>
                      </>
                    );

                    return ref.availability === 'missing' ? (
                      <div
                        className="flex items-center gap-4 rounded-xl border border-amber-200 bg-amber-50 p-4"
                        key={`${ref.path}-${refIndex}`}
                      >
                        {sourceBody}
                        <span className="material-symbols-outlined text-amber-500">block</span>
                      </div>
                    ) : (
                      <a
                        className="group flex cursor-pointer items-center gap-4 rounded-xl border border-slate-200 p-4 transition-colors hover:bg-slate-50"
                        href={sourceDocumentHref(ref.path, filePage)}
                        key={`${ref.path}-${refIndex}`}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {sourceBody}
                        <span className="material-symbols-outlined text-slate-300 group-hover:text-slate-600">open_in_new</span>
                      </a>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 p-4 text-sm text-slate-400">No source reference</div>
              )}
            </section>

            <section>
              <h4 className="mb-4 text-[11px] font-bold uppercase tracking-widest text-slate-400">Metadata</h4>
              <div className="mb-6 flex items-center gap-8">
                <div className="relative flex h-16 w-16 items-center justify-center">
                  <svg className="h-full w-full -rotate-90">
                    <circle className="text-slate-100" cx="32" cy="32" fill="transparent" r="28" stroke="currentColor" strokeWidth="6" />
                    <circle
                      className="text-emerald-500"
                      cx="32"
                      cy="32"
                      data-testid="confidence-donut"
                      fill="transparent"
                      r="28"
                      stroke="currentColor"
                      strokeDasharray={donutCircumference}
                      strokeDashoffset={donutCircumference - donutCircumference * confidence}
                      strokeLinecap="round"
                      strokeWidth="6"
                    />
                  </svg>
                  <span className="absolute text-sm font-black text-slate-800">
                    {displayedReview.confidence != null ? `${Math.round(displayedReview.confidence * 100)}%` : '—'}
                  </span>
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-800">AI Confidence Score</div>
                  <div className="text-xs text-slate-500">Based on source similarity</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {displayedReview.clusterKey ? (
                  <span className="rounded border border-slate-200 bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-600">
                    #{displayedReview.clusterKey.replace(/[:]/g, '-').toUpperCase()}
                  </span>
                ) : null}
                <span className="rounded border border-slate-200 bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-600">
                  #{displayedReview.diffOp.toUpperCase()}
                </span>
                <span className="rounded border border-slate-200 bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-600">
                  #CURRICULUM-SYNC
                </span>
              </div>
            </section>
          </div>
          )}
        </div>
      </main>

      {hasDiffs ? (
      <footer className="flex h-[64px] flex-shrink-0 items-center justify-between border-t border-slate-200 bg-white px-8">
        <button
          className="flex items-center gap-2 rounded border border-primary px-6 py-2.5 text-sm font-bold text-primary transition-colors hover:bg-slate-50 disabled:opacity-40"
          disabled={currentChangeIndex === 0}
          onClick={() => {
            const nextIndex = Math.max(0, currentChangeIndex - 1);
            setCurrentChangeIndex(nextIndex);
            setSelectedNodeId(diffs[nextIndex]?.targetId ?? null);
          }}
          type="button"
        >
          <span className="material-symbols-outlined">arrow_back</span>
          Previous Change
        </button>
        <div className="flex w-64 flex-col items-center gap-1.5">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400" data-testid="change-counter">
            Change {currentChangeIndex + 1} / {diffs.length}
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-primary"
              data-testid="review-progress"
              style={{ width: `${((currentChangeIndex + 1) / diffs.length) * 100}%` }}
            />
          </div>
        </div>
        <button
          className="flex items-center gap-2 rounded bg-primary px-6 py-2.5 text-sm font-bold text-white transition-colors hover:opacity-90 disabled:opacity-40"
          disabled={currentChangeIndex === diffs.length - 1}
          onClick={() => {
            const nextIndex = Math.min(diffs.length - 1, currentChangeIndex + 1);
            setCurrentChangeIndex(nextIndex);
            setSelectedNodeId(diffs[nextIndex]?.targetId ?? null);
          }}
          type="button"
        >
          Next Change
          <span className="material-symbols-outlined">arrow_forward</span>
        </button>
      </footer>
      ) : null}
    </div>
  );

  async function loadReview(options: { preserveBanner?: boolean } = {}) {
    if (!courseKey) {
      setItems([]);
      setBaseTree([]);
      setExpandedNodeIds(new Set());
      setCourseTitle('');
      setError('Structural review requires a course context.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    setStructuralWorkflowUnavailable(null);

    try {
      const [queue, groups, allCourses, runs] = await Promise.all([
        fetchQueueItems({ courseKey, ...(sectionId ? { sectionId } : {}) }),
        fetchGroups(courseKey),
        fetchCourses(),
        fetchBatchRuns(courseKey),
      ]);
      const structural = queue.items.filter((item) => item.taskType === 'structural' && !isNoSecondTry(item));
      const filtered = clusterKey ? structural.filter((item) => item.clusterKey === clusterKey) : structural;
      const selectedCourse = allCourses.find((course) => course.id === courseKey);
      const nextCourseTitle = selectedCourse?.title ?? courseKey;
      const defaultCourseKey = selectAuthoringCourseKey(allCourses);

      if (selectedCourse?.supportsStructuralWorkflow === false) {
        setItems([]);
        setBaseTree([]);
        setExpandedNodeIds(new Set());
        setCourseTitle(nextCourseTitle);
        setCurrentVersionLabel('Current Baseline');
        setProposedVersionLabel('Proposed Run');
        setCurrentChangeIndex(0);
        setSelectedNodeId(null);
        setStructuralWorkflowUnavailable({
          courseKey,
          courseTitle: nextCourseTitle,
          defaultCourseKey: defaultCourseKey || courseKey,
        });
        if (!options.preserveBanner) {
          setBanner(null);
        }
        return;
      }

      const activeRunId = filtered[0]?.runId;
      const activeRun = activeRunId ? runs.find((run) => run.runId === activeRunId) : undefined;
      const fullGroupTree = groupsToTreeNodes(groups);
      const groupDiffs = buildTreeDiff(fullGroupTree, filtered);

      // When a sectionId is provided, focus the tree on the common ancestor of all diffs.
      // When viewing the full course (no sectionId), show the entire structure.
      let nextBaseTree: TreeNode[];
      if (sectionId) {
        const focusRootId = deriveFocusRootId(fullGroupTree, groupDiffs);
        const requestedSectionNodeId = findTreeNodeIdByNumericId(fullGroupTree, sectionId);
        const resolvedRootNodeId = focusRootId ?? requestedSectionNodeId ?? null;
        nextBaseTree = resolvedRootNodeId
          ? await fetchStructureSubtree(courseKey, parseNumericNodeId(resolvedRootNodeId))
          : [];
      } else {
        // Full course view: use the groups tree directly (already fetched)
        nextBaseTree = fullGroupTree;
      }
      const nextDiffs = buildTreeDiff(nextBaseTree, filtered);
      const nextProposedTree = buildProposedTree(nextBaseTree, nextDiffs);

      setItems(filtered);
      setBaseTree(nextBaseTree);
      setExpandedNodeIds(buildInitialExpandedNodeIds(nextBaseTree, nextProposedTree));
      setCourseTitle(nextCourseTitle);
      setCurrentVersionLabel(activeRun?.courseKey ? `${activeRun.courseKey.toUpperCase()} Baseline` : `${courseKey.toUpperCase()} Baseline`);
      setProposedVersionLabel(activeRun?.runId ? `${activeRun.runId} Proposed` : 'Proposed Run');
      setCurrentChangeIndex(0);
      setSelectedNodeId(
        nextDiffs[0]?.targetId ?? findFirstSelectableNodeId(nextProposedTree) ?? findFirstSelectableNodeId(nextBaseTree) ?? null,
      );
      if (!options.preserveBanner) {
        setBanner(null);
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to load structural review');
      setItems([]);
      setBaseTree([]);
      setExpandedNodeIds(new Set());
      setStructuralWorkflowUnavailable(null);
      setCurrentVersionLabel('Current Baseline');
      setProposedVersionLabel('Proposed Run');
      setSelectedNodeId(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove() {
    if (!current) {
      return;
    }
    try {
      await approveItem(current.workItemId);
      advanceAfterMutation(`Approved ${current.workItemId}.`);
    } catch (reason) {
      await handleMutationError(reason, `Approval failed for ${current.workItemId}.`);
    }
  }

  async function handleApproveAll() {
    if (diffs.length === 0) return;
    if (!confirm(`Approve all ${diffs.length} structural changes?`)) return;

    let approved = 0;
    let failed = 0;
    for (const diff of diffs) {
      try {
        await approveItem(diff.workItemId);
        approved += 1;
      } catch {
        failed += 1;
      }
    }

    if (failed > 0) {
      setBanner({ tone: 'error', message: `Approved ${approved}, failed ${failed}. Refreshing.` });
    } else {
      setBanner({ tone: 'success', message: `Approved all ${approved} structural changes.` });
    }
    await loadReview({ preserveBanner: true });
  }

  async function handleCancel() {
    if (!current) {
      return;
    }
    try {
      await cancelItem(current.workItemId);
      advanceAfterMutation(`Rejected ${current.workItemId}.`);
    } catch (reason) {
      await handleMutationError(reason, `Rejection failed for ${current.workItemId}.`);
    }
  }

  async function handleMutationError(reason: unknown, fallbackMessage: string) {
    const status = reason instanceof ApiRequestError ? reason.status : undefined;
    if (status === 409) {
      setBanner({ tone: 'error', message: 'This change was already reviewed elsewhere. Refreshing structural review.' });
      await loadReview({ preserveBanner: true });
      return;
    }

    setBanner({
      tone: 'error',
      message: reason instanceof Error ? reason.message || fallbackMessage : fallbackMessage,
    });
  }

  function advanceAfterMutation(message: string) {
    if (!current) {
      return;
    }
    setBanner({ tone: 'success', message });
    setItems((currentItems) => currentItems.filter((item) => item.workItemId !== current.workItemId));
    setCurrentChangeIndex((index) => Math.min(index, Math.max(diffs.length - 2, 0)));
    setSelectedNodeId(null);
  }

  function toggleNodeExpansion(nodeId: string) {
    setExpandedNodeIds((currentExpandedNodeIds) => {
      const next = new Set(currentExpandedNodeIds);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }

  function handleNodeSelect(nodeId: string) {
    setSelectedNodeId(nodeId);
    const affectingDiff = findAffectingDiff(nodeId, diffIndex, proposedTreeIndex, currentTreeIndex);
    if (!affectingDiff) {
      return;
    }
    const nextIndex = diffs.findIndex((entry) => entry.workItemId === affectingDiff.workItemId);
    if (nextIndex >= 0) {
      setCurrentChangeIndex(nextIndex);
    }
  }
}

function CurrentTreePanel({
  currentVersionLabel,
  nodes,
  expandedNodeIds,
  onToggle,
  onSelect,
  selectedNodeId,
}: {
  currentVersionLabel: string;
  nodes: TreeNode[];
  expandedNodeIds: Set<string>;
  onToggle: (nodeId: string) => void;
  onSelect: (nodeId: string) => void;
  selectedNodeId: string | null;
}) {
  return (
    <div className="flex w-[30%] flex-col border-r border-slate-200 bg-slate-50/30" data-testid="current-structure-panel">
      <div className="flex items-center justify-between border-b border-slate-100 p-4">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Current Structure</span>
        <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
          {currentVersionLabel}
        </span>
      </div>
      <div className="flex-1 space-y-0.5 overflow-y-auto p-4">
        {nodes.map((node) => (
          <CurrentTreeRow
            depth={0}
            expandedNodeIds={expandedNodeIds}
            key={node.id}
            node={node}
            onSelect={onSelect}
            onToggle={onToggle}
            selectedNodeId={selectedNodeId}
          />
        ))}
      </div>
    </div>
  );
}

function CurrentTreeRow({
  node,
  depth,
  expandedNodeIds,
  onToggle,
  onSelect,
  selectedNodeId,
}: {
  node: TreeNode;
  depth: number;
  expandedNodeIds: Set<string>;
  onToggle: (nodeId: string) => void;
  onSelect: (nodeId: string) => void;
  selectedNodeId: string | null;
}) {
  const hasChildren = node.children.length > 0;
  const isExpanded = hasChildren && expandedNodeIds.has(node.id);
  const isSelected = selectedNodeId === node.id;

  return (
    <>
      <div className={isSelected ? 'relative z-10 -mx-3 rounded-sm bg-primary px-3 py-1 text-white shadow-sm' : ''} data-selected={isSelected ? 'true' : 'false'}>
        <div className={`flex items-center gap-2 py-1 ${isSelected ? 'text-white' : depth === 0 ? 'text-slate-700' : 'text-slate-600'}`} style={{ paddingLeft: depth * 20 }}>
          <ExpandToggle
            chevronTone={isSelected ? 'text-white/70' : depth === 0 ? 'text-slate-400' : 'text-slate-300'}
            hasChildren={hasChildren}
            isExpanded={isExpanded}
            label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.title} in current tree`}
            onToggle={() => onToggle(node.id)}
          />
          <button className="flex min-w-0 items-center gap-2 text-left" onClick={() => onSelect(node.id)} type="button">
            <span className={`material-symbols-outlined ${isSelected ? 'text-white/80' : 'text-slate-400'}`}>
              {node.type === 'group' ? 'folder' : 'menu_book'}
            </span>
            <span className="truncate text-sm">{node.title}</span>
          </button>
        </div>
      </div>
      {hasChildren && isExpanded ? (
        <div className="tree-line ml-2 space-y-0.5 pl-5">
          {node.children.map((child) => (
            <CurrentTreeRow
              depth={depth + 1}
              expandedNodeIds={expandedNodeIds}
              key={child.id}
              node={child}
              onSelect={onSelect}
              onToggle={onToggle}
              selectedNodeId={selectedNodeId}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

function ProposedTreePanel({
  proposedVersionLabel,
  nodes,
  diffIndex,
  expandedNodeIds,
  onToggle,
  onSelect,
  selectedNodeId,
}: {
  proposedVersionLabel: string;
  nodes: TreeNode[];
  diffIndex: Map<string, TreeDiffEntry>;
  expandedNodeIds: Set<string>;
  onToggle: (nodeId: string) => void;
  onSelect: (nodeId: string) => void;
  selectedNodeId: string | null;
}) {
  return (
    <div className="flex w-[35%] flex-col border-r border-slate-200" data-testid="proposed-structure-panel">
      <div className="flex items-center justify-between border-b border-slate-100 p-4">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Proposed Structure</span>
        <span className="rounded border border-emerald-100 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
          {proposedVersionLabel}
        </span>
      </div>
      <div className="flex-1 space-y-0.5 overflow-y-auto p-4">
        {nodes.map((node) => (
          <ProposedTreeRow
            depth={0}
            diffIndex={diffIndex}
            expandedNodeIds={expandedNodeIds}
            key={node.id}
            node={node}
            onSelect={onSelect}
            onToggle={onToggle}
            selectedNodeId={selectedNodeId}
          />
        ))}
      </div>
    </div>
  );
}

function ProposedTreeRow({
  node,
  depth,
  diffIndex,
  expandedNodeIds,
  onToggle,
  onSelect,
  selectedNodeId,
  inheritedDeleted = false,
}: {
  node: TreeNode;
  depth: number;
  diffIndex: Map<string, TreeDiffEntry>;
  expandedNodeIds: Set<string>;
  onToggle: (nodeId: string) => void;
  onSelect: (nodeId: string) => void;
  selectedNodeId: string | null;
  inheritedDeleted?: boolean;
}) {
  const directDiff = diffIndex.get(node.id);
  const diffOp = directDiff?.diffOp;
  const hasChildren = node.children.length > 0;
  const isExpanded = hasChildren && expandedNodeIds.has(node.id);
  const isSelected = selectedNodeId === node.id;
  const isDeleted = Boolean(inheritedDeleted || diffOp === 'deleted' || node.deleted);
  const effectiveDiffOp: DiffOp = isDeleted ? 'deleted' : diffOp ?? 'unchanged';
  const isSelectedRenamed = isSelected && effectiveDiffOp === 'renamed';

  return (
    <>
      <div className={isSelected ? 'relative z-10 -mx-4 rounded-sm bg-primary px-3 py-1 text-white shadow-sm' : ''} data-selected={isSelected ? 'true' : 'false'}>
        <div
          className={effectiveDiffOp === 'moved' || effectiveDiffOp === 'reordered'
            ? `flex items-center justify-between py-1 ${isSelected ? 'text-white' : 'text-slate-700'}`
            : `flex items-center gap-2 py-1 ${resolveProposedTextTone(effectiveDiffOp, isSelected, depth)}`}
          style={{ paddingLeft: depth * 20 }}
        >
          <div className="flex min-w-0 items-center gap-2">
            <ExpandToggle
              chevronTone={resolveChevronTone(isSelected, effectiveDiffOp, depth)}
              hasChildren={hasChildren}
              isExpanded={isExpanded}
              label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.title} in proposed tree`}
              onToggle={() => onToggle(node.id)}
            />
            <button className="flex min-w-0 items-center gap-2 text-left" onClick={() => onSelect(node.id)} type="button">
              <span className={`material-symbols-outlined ${resolveNodeIconTone(isSelected, effectiveDiffOp)}`}>
                {node.type === 'group' ? 'folder' : 'menu_book'}
              </span>
              <span className={resolveTitleClass(effectiveDiffOp, isSelected)}>{node.title}</span>
              {renderDiffBadge(effectiveDiffOp)}
            </button>
          </div>
          {effectiveDiffOp === 'moved' || effectiveDiffOp === 'reordered' ? (
            <span className={`material-symbols-outlined text-lg ${isSelected ? 'text-white/60' : 'text-blue-400'}`}>
              {effectiveDiffOp === 'reordered' ? 'swap_vert' : 'south_east'}
            </span>
          ) : isSelected ? (
            <span className="material-symbols-outlined text-lg text-white/50">edit</span>
          ) : null}
        </div>
      </div>
      {hasChildren && isExpanded ? (
        <div className={isSelectedRenamed ? 'tree-line-active -mx-4 ml-2 space-y-0.5 bg-primary/5 pb-2 pl-5' : 'tree-line ml-2 space-y-0.5 pl-5'}>
          {node.children.map((child) => (
            <ProposedTreeRow
              depth={depth + 1}
              diffIndex={diffIndex}
              expandedNodeIds={expandedNodeIds}
              inheritedDeleted={isDeleted}
              key={child.id}
              node={child}
              onSelect={onSelect}
              onToggle={onToggle}
              selectedNodeId={selectedNodeId}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

function ExpandToggle({
  hasChildren,
  isExpanded,
  label,
  chevronTone,
  onToggle,
}: {
  hasChildren: boolean;
  isExpanded: boolean;
  label: string;
  chevronTone: string;
  onToggle: () => void;
}) {
  if (!hasChildren) {
    return <span className="inline-block w-5" aria-hidden="true" />;
  }

  return (
    <button
      aria-expanded={isExpanded}
      aria-label={label}
      className={`flex h-5 w-5 items-center justify-center rounded ${chevronTone}`}
      onClick={onToggle}
      type="button"
    >
      <span className="material-symbols-outlined text-lg">{isExpanded ? 'expand_more' : 'chevron_right'}</span>
    </button>
  );
}

function renderDiffBadge(diffOp: DiffOp) {
  switch (diffOp) {
    case 'renamed':
      return <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[9px] font-black tracking-tighter text-white">RENAMED</span>;
    case 'added':
      return <span className="ml-1 rounded bg-emerald-100 px-1 py-0.5 text-[9px] font-bold uppercase tracking-tighter text-emerald-700">Added</span>;
    case 'deleted':
      return <span className="ml-1 rounded border border-red-100 bg-red-50 px-1 py-0.5 text-[9px] font-bold uppercase tracking-tighter text-red-500">Deleted</span>;
    case 'moved':
      return <span className="ml-1 rounded border border-blue-100 bg-blue-50 px-1 py-0.5 text-[9px] font-bold uppercase tracking-tighter text-blue-600">Moved</span>;
    case 'reordered':
      return <span className="ml-1 rounded border border-blue-100 bg-blue-50 px-1 py-0.5 text-[9px] font-bold uppercase tracking-tighter text-blue-600">Reordered</span>;
    case 'rewired':
      return <span className="ml-1 rounded border border-violet-200 bg-violet-50 px-1 py-0.5 text-[9px] font-bold uppercase tracking-tighter text-violet-700">Rewired</span>;
    default:
      return null;
  }
}

function resolveChevronTone(isSelected: boolean, diffOp: DiffOp, depth: number) {
  if (isSelected) {
    return 'text-white/60';
  }
  if (diffOp === 'deleted') {
    return 'text-slate-300';
  }
  if (diffOp === 'added') {
    return 'text-emerald-600';
  }
  return depth === 0 ? 'text-slate-400' : 'text-slate-300';
}

function resolveNodeIconTone(isSelected: boolean, diffOp: DiffOp) {
  if (isSelected) {
    return 'text-white/80';
  }
  if (diffOp === 'deleted') {
    return 'text-slate-300';
  }
  return 'text-slate-400';
}

function resolveProposedTextTone(diffOp: DiffOp, isSelected: boolean, depth: number) {
  if (isSelected) {
    return 'text-white';
  }
  if (diffOp === 'deleted') {
    return 'text-slate-400 italic';
  }
  if (diffOp === 'added') {
    return 'text-emerald-600';
  }
  if (diffOp === 'renamed') {
    return 'text-slate-800 font-medium';
  }
  if (diffOp === 'rewired') {
    return 'text-violet-700 font-medium';
  }
  return depth === 0 ? 'text-slate-700' : 'text-slate-600';
}

function resolveTitleClass(diffOp: DiffOp, isSelected: boolean) {
  if (diffOp === 'deleted') {
    return 'truncate text-sm italic line-through';
  }
  return isSelected ? 'truncate text-sm font-semibold' : 'truncate text-sm';
}

function diffOpBadgeClass(diffOp: string): string {
  switch (diffOp) {
    case 'renamed':
      return 'rounded bg-amber-500 px-2 py-0.5 text-[10px] font-black tracking-tight text-white';
    case 'added':
      return 'rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-bold tracking-tight text-emerald-700';
    case 'deleted':
      return 'rounded border border-red-100 bg-red-50 px-2 py-0.5 text-[10px] font-bold tracking-tight text-red-500';
    case 'moved':
    case 'reordered':
      return 'rounded border border-blue-100 bg-blue-50 px-2 py-0.5 text-[10px] font-bold tracking-tight text-blue-600';
    case 'rewired':
      return 'rounded border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-bold tracking-tight text-violet-700';
    default:
      return 'rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold tracking-tight text-slate-600';
  }
}

function buildInitialExpandedNodeIds(currentTree: TreeNode[], proposedTree: TreeNode[]) {
  const expandedNodeIds = new Set<string>();
  collectExpandableNodeIds(currentTree, expandedNodeIds);
  collectExpandableNodeIds(proposedTree, expandedNodeIds);
  return expandedNodeIds;
}

function collectExpandableNodeIds(nodes: TreeNode[], expandedNodeIds: Set<string>) {
  nodes.forEach((node) => {
    if (node.children.length > 0) {
      expandedNodeIds.add(node.id);
      collectExpandableNodeIds(node.children, expandedNodeIds);
    }
  });
}

function buildTreeIndex(nodes: TreeNode[]): TreeIndex {
  const index = new Map<string, { node: TreeNode; parentId: string | null }>();
  const visit = (branch: TreeNode[], parentId: string | null) => {
    branch.forEach((node) => {
      index.set(node.id, { node, parentId });
      visit(node.children, node.id);
    });
  };
  visit(nodes, null);
  return index;
}

function deriveFocusRootId(tree: TreeNode[], diffs: TreeDiffEntry[]) {
  const index = buildTreeIndex(tree);
  const relevantIds = diffs
    .flatMap((diff) => [diff.targetId, diff.currentParentId, diff.proposedParentId])
    .filter((nodeId): nodeId is string => typeof nodeId === 'string' && index.has(nodeId));

  if (!relevantIds.length) {
    return null;
  }

  const ancestorChains = relevantIds.map((nodeId) => buildAncestorChain(nodeId, index));
  const shortestChainLength = Math.min(...ancestorChains.map((chain) => chain.length));
  let focusRootId: string | null = null;

  for (let offset = 0; offset < shortestChainLength; offset += 1) {
    const candidate = ancestorChains[0]?.[ancestorChains[0].length - 1 - offset];
    if (candidate && ancestorChains.every((chain) => chain[chain.length - 1 - offset] === candidate)) {
      focusRootId = candidate;
      continue;
    }
    break;
  }

  return focusRootId;
}

function findTreeNodeIdByNumericId(nodes: TreeNode[], numericId: string) {
  const expectedNodeId = toTreeNodeId('group', Number(numericId));
  const visit = (branch: TreeNode[]): string | null => {
    for (const node of branch) {
      if (node.id === expectedNodeId) {
        return node.id;
      }
      const childMatch = visit(node.children);
      if (childMatch) {
        return childMatch;
      }
    }
    return null;
  };

  return Number.isFinite(Number(numericId)) ? visit(nodes) : null;
}

function buildAncestorChain(nodeId: string, index: TreeIndex) {
  const chain: string[] = [];
  let cursor = index.get(nodeId);
  while (cursor) {
    chain.push(cursor.node.id);
    if (!cursor.parentId) {
      break;
    }
    cursor = index.get(cursor.parentId);
  }
  return chain;
}

function groupsToTreeNodes(groups: GroupNode[], parentId: string | null = null): TreeNode[] {
  return groups.map((group, index) => ({
    id: toTreeNodeId('group', group.id),
    title: group.title,
    parentId,
    ordinal: index + 1,
    type: 'group',
    children: groupsToTreeNodes(group.children, toTreeNodeId('group', group.id)),
  }));
}

function parseNumericNodeId(nodeId: string) {
  const parts = nodeId.split(':');
  const raw = parts[parts.length - 1];
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) {
    throw new Error(`Expected numeric node id, received ${nodeId}`);
  }
  return parsed;
}

function findFirstSelectableNodeId(nodes: TreeNode[]): string | null {
  for (const node of nodes) {
    return node.id;
  }
  return null;
}

function resolveSelectedNodeReview(
  selectedNodeId: string | null,
  currentTreeIndex: TreeIndex,
  proposedTreeIndex: TreeIndex,
  diffIndex: Map<string, TreeDiffEntry>,
): SelectedNodeReview | null {
  if (!selectedNodeId) {
    return null;
  }

  const node = proposedTreeIndex.get(selectedNodeId)?.node ?? currentTreeIndex.get(selectedNodeId)?.node;
  if (!node) {
    return null;
  }

  const directDiff = diffIndex.get(selectedNodeId);
  if (directDiff) {
    return {
      node,
      appliedDiff: directDiff,
      diffOp: directDiff.diffOp,
      title: node.title,
      rationale: directDiff.rationale,
      sourceRefs: directDiff.sourceRefs,
      confidence: directDiff.confidence ?? null,
      clusterKey: directDiff.clusterKey,
      inheritedFromTitle: null,
      isDirect: true,
    };
  }

  const inheritedDiff = findAffectingDiff(selectedNodeId, diffIndex, proposedTreeIndex, currentTreeIndex);
  if (inheritedDiff) {
    const inheritedNode =
      proposedTreeIndex.get(inheritedDiff.targetId ?? '')?.node ??
      currentTreeIndex.get(inheritedDiff.targetId ?? '')?.node;

    return {
      node,
      appliedDiff: inheritedDiff,
      diffOp: inheritedDiff.diffOp,
      title: node.title,
      rationale: buildInheritedRationale(node, inheritedDiff, inheritedNode?.title),
      sourceRefs: inheritedDiff.sourceRefs,
      confidence: inheritedDiff.confidence ?? null,
      clusterKey: inheritedDiff.clusterKey,
      inheritedFromTitle: inheritedNode?.title ?? null,
      isDirect: false,
    };
  }

  return {
    node,
    appliedDiff: null,
    diffOp: 'unchanged',
    title: node.title,
    rationale: `${node.title} does not have a direct structural change in this review. Select a renamed, moved, added, or deleted node to inspect its source-backed rationale.`,
    sourceRefs: [],
    confidence: null,
    clusterKey: null,
    inheritedFromTitle: null,
    isDirect: false,
  };
}

function findAffectingDiff(
  nodeId: string,
  diffIndex: Map<string, TreeDiffEntry>,
  proposedTreeIndex: TreeIndex,
  currentTreeIndex: TreeIndex,
) {
  const directDiff = diffIndex.get(nodeId);
  if (directDiff) {
    return directDiff;
  }

  for (const index of [proposedTreeIndex, currentTreeIndex]) {
    let cursor = index.get(nodeId);
    while (cursor?.parentId) {
      const parentDiff = diffIndex.get(cursor.parentId);
      if (parentDiff) {
        return parentDiff;
      }
      cursor = index.get(cursor.parentId);
    }
  }

  return null;
}

function buildInheritedRationale(node: TreeNode, diff: TreeDiffEntry, inheritedFromTitle?: string) {
  switch (diff.diffOp) {
    case 'deleted':
      return `${node.title} is marked for deletion because ${inheritedFromTitle ?? 'its parent branch'} is retired in the proposed structure. ${diff.rationale}`;
    case 'moved':
    case 'reordered':
      return `${node.title} stays nested beneath ${inheritedFromTitle ?? 'its selected parent'}, which is being ${diff.diffOp}. ${diff.rationale}`;
    case 'renamed':
      return `${node.title} stays nested beneath ${inheritedFromTitle ?? 'its selected parent'}, which is being renamed. ${diff.rationale}`;
    case 'added':
      return `${node.title} is part of the net-new subtree rooted at ${inheritedFromTitle ?? 'its selected parent'}. ${diff.rationale}`;
    default:
      return diff.rationale;
  }
}

function ReviewState({
  actionHref,
  actionLabel,
  message,
  tone,
}: {
  actionHref?: string;
  actionLabel?: string;
  message: string;
  tone: 'error' | 'neutral';
}) {
  return (
    <div className={tone === 'error'
      ? 'grid min-h-[18rem] place-items-center rounded-[1.5rem] border border-rose-200 bg-rose-50 px-6 text-center text-sm text-rose-800 shadow-sm'
      : 'grid min-h-[18rem] place-items-center rounded-[1.5rem] border border-slate-200 bg-white px-6 text-center text-sm text-slate-500 shadow-sm'}>
      <div className="grid max-w-xl gap-4">
        <span className={tone === 'neutral' && !actionHref ? 'animate-pulse' : ''}>{message}</span>
        {actionHref && actionLabel ? (
          <Link
            className={`mx-auto rounded bg-primary px-4 py-2 text-sm font-semibold transition-colors hover:opacity-90 ${PRIMARY_ACTION_LINK_STYLES}`}
            to={actionHref}
          >
            {actionLabel}
          </Link>
        ) : null}
      </div>
    </div>
  );
}
